import React, { useCallback, useEffect } from 'react';

import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';

import {
	VoiceDictationState,
	VoiceDictationStopReason,
	type VoiceDictationResult,
} from '../../../speech-to-text/v1/effects/WebSpeechToTextModels';
import { useTranscriptionToEditorText } from '../../../speech-to-text/v1/hooks/useTranscriptionToEditorText';
import { useVoiceDictation } from '../../../speech-to-text/v1/hooks/useVoiceDictation';
import { VoiceAcceptButton } from '../../../speech-to-text/v1/ui/voice-accept-button';
import { VoiceRejectButton } from '../../../speech-to-text/v1/ui/voice-reject-button';
import type { AiSpeechToTextPlugin } from '../../aiSpeechToTextPluginType';
import { aiSpeechToTextPluginKey } from '../../pluginKey';

type AISpeechToTextContentProps = {
	api: ExtractInjectionAPI<AiSpeechToTextPlugin> | undefined;
	cloudId: string;
};

export const AISpeechToTextContent = ({
	api,
	cloudId,
}: AISpeechToTextContentProps): JSX.Element | null => {
	const { isListening } = useSharedPluginStateWithSelector(api, ['aiSpeechToText'], (states) => ({
		isListening: states.aiSpeechToTextState?.isListening ?? false,
	}));

	const { handleTranscriptUpdate, rejectTranscriptedText, disposeTranscriptedText } =
		useTranscriptionToEditorText();

	const onVoiceDictationUpdate = useCallback(
		(result: VoiceDictationResult) => {
			switch (result.state) {
				case VoiceDictationState.Recording:
					if (!api?.core?.actions?.execute) {
						return;
					}
					api.core.actions.execute(({ tr }) => handleTranscriptUpdate(result.transcription, tr));
					break;
				case VoiceDictationState.Stopped:
					if (result.stopReason === VoiceDictationStopReason.TimeoutAccepted) {
						disposeTranscriptedText();
						api?.core.actions.execute(({ tr }) => {
							tr.setMeta(aiSpeechToTextPluginKey, { isListening: false });
							return tr;
						});
					}
					break;
				default:
					break;
			}
		},
		[handleTranscriptUpdate, api, disposeTranscriptedText],
	);

	const [, { startRecording, stopRecording }] = useVoiceDictation(onVoiceDictationUpdate, cloudId);

	const handleAccept = useCallback(() => {
		disposeTranscriptedText();
		stopRecording(VoiceDictationStopReason.StoppedAccepted);
		api?.core.actions.execute(({ tr }) => {
			tr.setMeta(aiSpeechToTextPluginKey, { isListening: false });
			return tr;
		});
	}, [stopRecording, disposeTranscriptedText, api]);

	const handleReject = useCallback(() => {
		stopRecording(VoiceDictationStopReason.StoppedRejected);
		api?.core.actions.execute(({ tr }) => {
			tr.setMeta(aiSpeechToTextPluginKey, { isListening: false });
			return rejectTranscriptedText(tr);
		});
	}, [stopRecording, rejectTranscriptedText, api]);

	useEffect(() => {
		if (isListening) {
			startRecording();
		} else {
			stopRecording();
		}
	}, [isListening, startRecording, stopRecording]);

	if (!isListening) {
		return null;
	}

	return (
		<div>
			<VoiceAcceptButton onClick={handleAccept} />
			<VoiceRejectButton onClick={handleReject} />
		</div>
	);
};
